import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Layers, Link as LinkIcon, FolderSearch, X, FileText, Loader2 } from 'lucide-react';
import { appsApi, AppCreate } from '../../lib/api/apps';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import * as Sentry from '@sentry/react';

const appSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z.string().min(1, 'Slug is required').max(255).regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, numbers, and hyphens'),
  description: z.string().optional(),
  server_id: z.string().min(1, 'Server is required'),
  docker_compose_path: z.string().optional(),
  service_name: z.string().optional(),
  git_repo: z.string().optional(),
  git_branch: z.string().optional(),
  domain: z.string().optional(),
});

type AppFormData = z.infer<typeof appSchema>;

export function NewAppPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Get pre-fill data from URL params (when linking from container)
  const containerName = searchParams.get('container');
  const serverId = searchParams.get('server');
  const containerImage = searchParams.get('image');
  const composePath = searchParams.get('compose_path');
  const containerDomain = searchParams.get('domain');
  const composeService = searchParams.get('service');
  const isLinkingContainer = !!(containerName && serverId);

  // File browser state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseFiles, setBrowseFiles] = useState<string[]>([]);
  const [browseError, setBrowseError] = useState<string | null>(null);

  // Services state - now with type for grouping
  interface ServiceItem {
    name: string;
    type: 'static' | 'docker';
  }
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const { data: servers } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list({ limit: 100 }),
  });

  // Find server name for display
  const selectedServer = useMemo(() => {
    return servers?.items.find(s => s.id === serverId);
  }, [servers, serverId]);

  const createApp = useMutation({
    mutationFn: (data: AppCreate) => appsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      navigate('/apps');
    },
  });

  // Generate a nice name from container name
  const generateNameFromContainer = (container: string): string => {
    // Remove common prefixes/suffixes and format nicely
    return container
      .replace(/^(tch-|app-|svc-)/, '')
      .replace(/(-api|-web|-service|-app)$/, '')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AppFormData>({
    resolver: zodResolver(appSchema),
    defaultValues: {
      git_branch: 'main',
      name: containerName ? generateNameFromContainer(containerName) : '',
      slug: containerName || '',
      server_id: serverId || '',
      service_name: composeService || containerName || '',
      docker_compose_path: composePath || '',
      domain: containerDomain || '',
    },
  });

  // Set values when params are available
  useEffect(() => {
    if (containerName) {
      setValue('name', generateNameFromContainer(containerName));
      setValue('slug', containerName.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
      setValue('service_name', composeService || containerName);
    }
    if (serverId) {
      setValue('server_id', serverId);
    }
    if (composePath) {
      setValue('docker_compose_path', composePath);
    }
    if (containerDomain) {
      setValue('domain', containerDomain);
    }
  }, [containerName, serverId, composePath, containerDomain, composeService, setValue]);

  // Auto-generate slug from name (only when not linking)
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isLinkingContainer) {
      const value = e.target.value;
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setValue('slug', slug);
    }
  };

  const onSubmit = (data: AppFormData) => {
    createApp.mutate(data);
  };

  // Watch server_id and docker_compose_path for dependent actions
  const watchServerId = watch('server_id');
  const watchComposePath = watch('docker_compose_path');

  // When server changes, auto-populate compose path from server if available
  useEffect(() => {
    if (watchServerId && !isLinkingContainer) {
      const server = servers?.items.find(s => s.id === watchServerId);
      if (server?.docker_compose_path && !watchComposePath) {
        setValue('docker_compose_path', server.docker_compose_path);
      }
    }
  }, [watchServerId, servers, isLinkingContainer, watchComposePath, setValue]);

  // Load both static apps AND docker services when server is selected
  useEffect(() => {
    const loadAllServices = async () => {
      if (!watchServerId) {
        setServices([]);
        return;
      }

      setServicesLoading(true);
      const allServices: ServiceItem[] = [];

      try {
        // Always load static apps
        const staticResult = await serversApi.getStaticApps(watchServerId);
        if (staticResult.apps?.length) {
          staticResult.apps.forEach(app => {
            allServices.push({ name: app, type: 'static' });
          });
        }

        // Also load docker services if compose path exists
        if (watchComposePath) {
          const dockerResult = await serversApi.getComposeServices(watchServerId, watchComposePath);
          if (dockerResult.services?.length) {
            dockerResult.services.forEach(service => {
              // Avoid duplicates
              if (!allServices.find(s => s.name === service)) {
                allServices.push({ name: service, type: 'docker' });
              }
            });
          }
        }

        setServices(allServices);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: 'apps',
            operation: 'load_services',
            severity: 'warning',
          },
          extra: {
            serverId: watchServerId,
            composePath: watchComposePath,
          },
        });
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    };
    loadAllServices();
  }, [watchServerId, watchComposePath]);

  // Browse for compose files on server
  const handleBrowseFiles = async () => {
    const currentServerId = watch('server_id');
    if (!currentServerId) return;

    setShowFileBrowser(true);
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const result = await serversApi.browseFiles(currentServerId, '/', '*docker-compose*.yml');
      setBrowseFiles(result.files);
      if (result.error) {
        setBrowseError(result.error);
      }
    } catch (error: any) {
      setBrowseError(error.response?.data?.error?.message || 'Failed to browse files');
    } finally {
      setBrowseLoading(false);
    }
  };

  const handleSelectFile = (filePath: string) => {
    setValue('docker_compose_path', filePath);
    setShowFileBrowser(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/apps')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isLinkingContainer ? 'Link Container to App' : 'New Application'}
        </h1>
      </div>

      {/* Container Info Banner when linking */}
      {isLinkingContainer && (
        <Card className="border-primary-200 bg-primary-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary-100">
                <Layers className="h-6 w-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-lg font-medium text-primary-900">
                  <LinkIcon className="h-4 w-4" />
                  Linking Container
                </div>
                <div className="text-sm text-primary-700 mt-1">
                  <span className="font-mono bg-primary-100 px-2 py-0.5 rounded">{containerName}</span>
                  {containerImage && (
                    <span className="ml-2 text-primary-600">({containerImage})</span>
                  )}
                </div>
                {selectedServer && (
                  <div className="text-sm text-primary-600 mt-1">
                    Server: {selectedServer.name} ({selectedServer.host})
                  </div>
                )}
                {(composePath || containerDomain) && (
                  <div className="text-sm text-primary-600 mt-2 space-y-1">
                    {composePath && (
                      <div>
                        <span className="text-primary-500">Compose Path:</span>{' '}
                        <span className="font-mono text-xs bg-primary-100 px-1.5 py-0.5 rounded">{composePath}</span>
                      </div>
                    )}
                    {containerDomain && (
                      <div>
                        <span className="text-primary-500">Domain:</span>{' '}
                        <span className="font-mono text-xs bg-primary-100 px-1.5 py-0.5 rounded">{containerDomain}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {isLinkingContainer ? 'Confirm App Details' : 'Application Details'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Name"
              placeholder="My App"
              error={errors.name?.message}
              {...register('name', { onChange: handleNameChange })}
            />

            <Input
              label="Slug"
              placeholder="my-app"
              error={errors.slug?.message}
              {...register('slug')}
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Application description..."
                {...register('description')}
              />
            </div>

            {/* Server - read-only when linking */}
            {isLinkingContainer ? (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Server</label>
                <input
                  type="hidden"
                  {...register('server_id')}
                />
                <div className="flex w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {selectedServer ? `${selectedServer.name} (${selectedServer.host})` : 'Loading...'}
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Server</label>
                <select
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  {...register('server_id')}
                >
                  <option value="">Select a server</option>
                  {servers?.items.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name} ({server.host})
                    </option>
                  ))}
                </select>
                {errors.server_id && (
                  <p className="text-sm text-red-600">{errors.server_id.message}</p>
                )}
              </div>
            )}

            {/* Docker Compose Path with Browse */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Docker Compose Path (optional)
              </label>
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="/home/ubuntu/app/docker-compose.yml"
                  {...register('docker_compose_path')}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBrowseFiles}
                  disabled={!watchServerId}
                  title={!watchServerId ? 'Select a server first' : 'Browse for compose files'}
                >
                  <FolderSearch className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {servicesLoading
                  ? 'Loading services...'
                  : services.length > 0
                    ? `Found ${services.filter(s => s.type === 'static').length} static app(s) and ${services.filter(s => s.type === 'docker').length} Docker service(s)`
                    : watchServerId ? 'No services found' : 'Select a server to load services'}
              </p>
            </div>

            {/* File Browser Modal */}
            {showFileBrowser && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Select Docker Compose File</h4>
                  <Button size="sm" variant="ghost" onClick={() => setShowFileBrowser(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {browseLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching for docker-compose files...
                  </div>
                ) : browseError ? (
                  <p className="text-red-600">{browseError}</p>
                ) : browseFiles.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {browseFiles.map((file) => (
                      <button
                        key={file}
                        type="button"
                        onClick={() => handleSelectFile(file)}
                        className="w-full text-left p-2 rounded hover:bg-gray-100 text-sm font-mono text-gray-700 flex items-center gap-2"
                      >
                        <FileText className="h-4 w-4 text-gray-400" />
                        {file}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No docker-compose files found</p>
                )}
              </div>
            )}

            {/* Service Name - dropdown when services available */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                {isLinkingContainer ? "Container/Service Name" : "Service / App"}
              </label>
              {services.length > 0 ? (
                <select
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  {...register('service_name')}
                >
                  <option value="">Select a service or app</option>
                  {/* Static Apps Group */}
                  {services.filter(s => s.type === 'static').length > 0 && (
                    <optgroup label="Static Apps">
                      {services.filter(s => s.type === 'static').map((service) => (
                        <option key={`static-${service.name}`} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Docker Services Group */}
                  {services.filter(s => s.type === 'docker').length > 0 && (
                    <optgroup label="Docker Services">
                      {services.filter(s => s.type === 'docker').map((service) => (
                        <option key={`docker-${service.name}`} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              ) : (
                <input
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="tch-console"
                  {...register('service_name')}
                />
              )}
              {servicesLoading && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading services...
                </p>
              )}
            </div>

            <Input
              label="Git Repository (optional)"
              placeholder="https://github.com/user/repo.git"
              {...register('git_repo')}
            />

            <Input
              label="Git Branch"
              placeholder="main"
              {...register('git_branch')}
            />

            <Input
              label="Domain (optional)"
              placeholder="app.example.com"
              {...register('domain')}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/apps')}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createApp.isPending}>
                {isLinkingContainer ? (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link Container
                  </>
                ) : (
                  'Create App'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
